// Copyright (c) Docugami, Inc. All rights reserved.

import { IDocumentCommand } from "components/file_management/DocumentCommand";
import { DocSetMergeChooser } from "components/myContentExplorer/DocSetChooser";
import { ClusterInfo } from "networking/api";
import { useState } from 'react';
import { selectIsLimitedOrganization } from "store/AccountInfoHelpers";
import { useMergeDocsetsMutation } from "store/frontend/frontendSlice";
import { useAppSelector } from "./redux";
import { DocSetExplorerChooserOverlay } from "components/myContentExplorer/DocSetAssignment";
import { useWaitCursor } from "./WaitCursor";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { Stack } from "@fluentui/react";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { mapVisibleUserClusters } from "store/state_helpers";

export function useMergeDocSetsCommand(docSet: ClusterInfo, onComplete?: (error?: any) => void): IDocumentCommand {
    const [isActive, setIsActive] = useState(false);
    const [docSetsToMerge, setMergeDocSets] = useState<string[]>();
    const userDocSets = useAppSelector(state => mapVisibleUserClusters(state.clusters));
    const disabled = useAppSelector(selectIsLimitedOrganization);

    const [triggerMerge, mergeData] = useMergeDocsetsMutation();

    useWaitCursor(mergeData.isLoading);

    async function mergeDocsets() {
        if (!docSetsToMerge?.length) {
            return;
        }

        const response = await triggerMerge({
            targetDocsetId: docSet.id!,
            docsetIds: docSetsToMerge
        });

        setIsActive(false);
        setMergeDocSets(undefined);
        onComplete?.((response as { error: FetchBaseQueryError }).error);
    }

    function getVisual() {
        return (
            <DocSetExplorerChooserOverlay
                title={<>Choose one or more doc sets to merge into <b>{docSet.label}</b>:</>}
                disabled={docSet.isDocugamiCluster || disabled}
                onDismiss={() => setIsActive(false)}>
                <div style={{ width: '40%', maxHeight: '80%' }}>
                    <DocSetMergeChooser docSets={userDocSets} targetDocsetId={docSet.id!} onSelectItem={setMergeDocSets} onCancel={() => setIsActive(false)} />
                </div>
                <WarningModal hidden={!isActive || !docSetsToMerge?.length} docSetIds={docSetsToMerge ?? []} onConfirm={mergeDocsets} onCancel={() => setMergeDocSets(undefined)} />
            </DocSetExplorerChooserOverlay>
        )
    }

    return {
        id: "MergeDocsetsCommand",
        isActive: isActive,
        label: "Merge",
        isEnabled: !docSet.isDocugamiCluster && userDocSets.length > 1 && !disabled,
        visual: getVisual(),
        execute: () => setIsActive(true)
    }
}


interface WarningModalProps {
    hidden?: boolean;
    docSetIds: string[];
    onConfirm: () => void;
    onCancel: () => void;
}

const WarningModal = (props: WarningModalProps): JSX.Element => {
    const docSets = useAppSelector(state => state.clusters.indexed);

    return (
        <ConfirmationDialog
            hidden={props.hidden}
            title={"CONFIRM DOC SET MERGE?"}
            confirmButtonText={"YES, CONTINUE"}
            dismissButtonText={"NO, CANCEL"}
            dismissedCallback={props.onCancel}
            confirmedCallback={props.onConfirm}>
            <Stack>
                <span>Merging these doc sets will cause the following doc sets and all related project data to be deleted:</span>
                <br />
                {props.docSetIds.map(docSetId => <span><b>{docSets[docSetId]?.clusterInfo.label}</b></span>)}
                <br />
                <span>You can not undo this action.</span>
            </Stack>
        </ConfirmationDialog>
    )
}